// The Google tag (gtag.js) loader, and the decision about when it may load at all.
//
// WHY THIS EXISTS. The tag used to be four hard-coded lines in index.html, so it
// loaded from every environment that ever served the shell — a developer's
// machine, a preview deployment, a test runner — all writing into the one
// production GA4 property. Measured over 30 days, `localhost` and `127.0.0.1`
// accounted for 92% of events in that property.
//
// THE ORIGINAL DIAGNOSIS HERE WAS WRONG, and is corrected rather than deleted
// because it was quoted as fact for two months (#1093). This comment used to
// argue that most of that 92% was third-party "measurement-ID spam" — someone
// posting to a public measurement ID from outside our code — and concluded that
// this guard was "hygiene, not a spam defence".
//
// It was our own traffic. The evidence is that it STOPPED, completely, on the
// day this guard shipped (2026-07-28) and stayed at zero across 139 subsequent
// CI runs. Nothing we deployed could have stopped an external sender; only our
// own browsers were ever writing those events. The signals read as "not us" —
// one page_view per brand-new session, no `v2_*` events, dispersed datacenter
// cities, uniform Windows/Chrome — are equally the signature of automated test
// browsers, which is what they were.
//
// So this guard is the fix, not merely hygiene. What it genuinely cannot do is
// stop a real third party who obtains the id, since the id is public in any
// page's source and ingestion is unauthenticated. That risk is real and
// unaddressed; it just was not what happened here.
//
// FAILS OFF. A missing, empty, malformed or placeholder id loads nothing. There
// is deliberately no fallback to the previously hard-coded id: a silent fallback
// would recreate exactly the behaviour this replaces, and would do it invisibly.

import { resolveAiReferrer } from "./aiReferrer";

const GTAG_SRC = "https://www.googletagmanager.com/gtag/js";

// Hostnames permitted to write to the production property. Exact matches only —
// a suffix test (`endsWith('filterproof.ai')`) would admit
// `filterproof.ai.attacker.example`, and a preview host is not production.
export const PRODUCTION_HOSTNAMES = Object.freeze(["filterproof.ai", "www.filterproof.ai"]);

// A GA4 measurement id: `G-` plus at least eight uppercase alphanumerics.
const MEASUREMENT_ID_RE = /^G-[A-Z0-9]{8,}$/;

// Documentation placeholders that must never reach the network.
const PLACEHOLDERS = Object.freeze(["G-XXXXXXXXXX", "GTM-XXXXXXXX", "G-", "TODO", "CHANGEME"]);

export function isValidMeasurementId(raw) {
  if (typeof raw !== "string") return false;
  const v = raw.trim();
  if (!v || PLACEHOLDERS.includes(v)) return false;
  return MEASUREMENT_ID_RE.test(v);
}

export function isProductionHost(hostname) {
  return typeof hostname === "string" && PRODUCTION_HOSTNAMES.includes(hostname);
}

/**
 * Whether the Google tag may load here. All three must hold; the reason is
 * returned rather than logged so callers and tests can assert on it.
 */
export function tagDecision({ measurementId, hostname, isProdBuild }) {
  if (!isProdBuild) return { load: false, reason: "not_production_build" };
  if (!isProductionHost(hostname)) return { load: false, reason: "non_production_host" };
  if (!isValidMeasurementId(measurementId)) return { load: false, reason: "invalid_measurement_id" };
  return { load: true, reason: "ok", measurementId: measurementId.trim() };
}

// ------------------------------------------------------- page_location scrub
//
// gtag's automatic page_view sends `page_location` = the full current URL,
// query string and fragment included. Our own URLs carry single-use auth
// secrets, so a GA4 page-path report showed rows like:
//
//   /auth/callback?code=627f6368-97a0-4b71-82d5-63c5fdb3aab9      (PKCE code)
//   /auth/callback?token_hash=pkce_b85267…&type=email             (magic link)
//   /?v2checkout=cs_live_a1irALbjHM9bKEMkM8PYBXR7rGnb9ale…        (Stripe session)
//
// Every one of those is single-use and long expired, so this is not a live
// credential exposure — but they were written into a third-party analytics
// product, retained for the property's retention window, and readable by anyone
// with report access. Secrets do not belong in analytics regardless of whether
// they still work.
//
// AuthCallback and V2CheckoutReturn already scrub the address bar via
// replaceState, which keeps these out of history and referrers. That happens in
// an effect, after React mounts — by which time gtag has already sent the
// automatic page_view for the initial URL. So the scrub has to happen here too,
// at config time, on the value gtag is about to report.
//
// DENY, NOT ALLOW, for the query. An allowlist would silently drop gclid, utm_*
// and every future attribution parameter, quietly destroying the paid-traffic
// reporting this work depends on. The one exception is `next`, which is
// caller-controlled free text: it is kept only when it names a known workspace
// destination, and dropped otherwise.
const SENSITIVE_PARAMS = Object.freeze([
  "code", "token_hash", "token", "otp", "access_token", "refresh_token",
  "id_token", "provider_token", "session_id", "v2checkout", "email", "state",
]);

// Not secret, but poisonous to reports: a cache-busting timestamp appended to
// navigations, which splits one page across dozens of distinct path values.
const NOISE_PARAMS = Object.freeze(["__v"]);

// Kept only when it names a real destination — see NEXT_ALLOWLIST in routes.js.
// Duplicated as literals rather than imported so this module stays dependency
// free and loadable before the router; the test asserts the two agree.
const NEXT_SAFE_VALUES = Object.freeze(["/workspace/fit", "/workspace/collect"]);

/**
 * The URL gtag may report: same origin and path, secrets and noise removed,
 * fragment always dropped.
 *
 * The fragment goes unconditionally. Supabase's implicit flow returns
 * `#access_token=…&refresh_token=…`, and no page of ours needs a fragment
 * reported, so there is nothing to weigh.
 */
export function scrubbedPageLocation(href) {
  let url;
  try {
    url = new URL(String(href));
  } catch {
    return null; // unparseable — report nothing rather than guess
  }
  url.hash = "";
  for (const key of [...SENSITIVE_PARAMS, ...NOISE_PARAMS]) url.searchParams.delete(key);
  const next = url.searchParams.get("next");
  if (next !== null && !NEXT_SAFE_VALUES.includes(next)) url.searchParams.delete("next");
  return url.toString();
}

/**
 * Load and configure the Google tag exactly once.
 *
 * Idempotent by a module-level flag AND by a DOM check, because React
 * StrictMode double-invokes effects, hot reload re-executes modules, and a
 * remount must not append a second script. Returns the decision either way.
 */
let loaded = false;

export function loadGoogleTag({
  measurementId = import.meta.env?.VITE_GA4_MEASUREMENT_ID,
  hostname = typeof window !== "undefined" ? window.location.hostname : "",
  isProdBuild = Boolean(import.meta.env?.PROD),
  doc = typeof document !== "undefined" ? document : null,
  win = typeof window !== "undefined" ? window : null,
} = {}) {
  const decision = tagDecision({ measurementId, hostname, isProdBuild });
  if (!decision.load || !doc || !win) return decision;
  if (loaded || doc.querySelector(`script[data-fp-gtag="1"]`)) {
    return { ...decision, alreadyLoaded: true };
  }

  win.dataLayer = win.dataLayer || [];
  // The canonical gtag shim. `arguments` is required — gtag reads the raw
  // arguments object, so a rest parameter changes what GA receives.
  function gtag() { win.dataLayer.push(arguments); } // eslint-disable-line prefer-rest-params
  win.gtag = win.gtag || gtag;

  const s = doc.createElement("script");
  s.async = true;
  s.src = `${GTAG_SRC}?id=${encodeURIComponent(decision.measurementId)}`;
  s.setAttribute("data-fp-gtag", "1");
  doc.head.appendChild(s);

  win.gtag("js", new Date());
  // send_page_view stays DEFAULT (on): enhanced measurement is what records the
  // real-route pageviews, and the shell emits no manual page_view of its own —
  // see V2Shell, where a second synthetic pageview was removed in #1079.
  //
  // page_location is pinned to the scrubbed URL so the automatic page_view for
  // THIS load cannot carry an auth code, magic-link hash or Stripe session id.
  // That initial hit is the one that matters: it is the only one that sees the
  // URL a redirect landed on. Subsequent history-based pageviews re-read
  // location, by which point AuthCallback and V2CheckoutReturn have already
  // replaceState'd the secret out of the address bar.
  const config = {};
  const scrubbed = scrubbedPageLocation(win.location?.href);
  if (scrubbed) config.page_location = scrubbed;
  // Which AI assistant, if any, sent this visit. GA4 already records
  // page_referrer and the full page_location (utm parameters included), so
  // this adds no new data about the person — it only labels the visit with a
  // stable name (chatgpt, claude, perplexity, …) so the traffic can be
  // reported without a hostname-or-utm regex in every exploration. An explicit
  // `utm_source` naming an assistant wins (ChatGPT search appends
  // utm_source=chatgpt.com and often sends no referrer); the referrer host is
  // the fallback. Absent for every other visit. See src/lib/aiReferrer.js and
  // docs/geo/analytics.md, including the GA4 custom-dimension step that must
  // be done in the property before the parameter shows up in reports.
  const aiReferrer = resolveAiReferrer({ referrer: doc?.referrer, search: win.location?.search });
  if (aiReferrer) config.ai_referrer = aiReferrer;
  win.gtag("config", decision.measurementId, config);

  loaded = true;
  return { ...decision, alreadyLoaded: false };
}

// Test seam only.
export function __resetGoogleTagForTests() { loaded = false; }
